/**
 * BreakdownSectionV2 Component
 *
 * Displays breakdown analysis of SPLH data with comparison support.
 * Tabbed views for Daypart, Day of Week, Top Performers, and Bottom Performers.
 *
 * Features:
 * - Daypart and Day of Week column charts showing current SPLH
 * - Tooltips with comparison SPLH and delta percentage
 * - Top/Bottom performers tables with eligibility indicators
 * - TanStack Table v8 with standard HTML table elements for performers
 *
 * Tab Order:
 * 1. Daypart (chart)
 * 2. Day of Week (chart)
 * 3. Top Performers (table)
 * 4. Bottom Performers (table)
 *
 * @module pages/common/dashboards/SPLHDashboard/components/BreakdownSectionV2
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Box, Tabs, Tab, Typography, Skeleton, Chip, Tooltip } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Block as ExcludedIcon,
} from '@mui/icons-material';
import { formatSPLH, formatCurrency, formatNumber, formatPercentage } from '../../utils/formatUtils';
import DaypartChartV2 from './components/DaypartChartV2';
import DayOfWeekChartV2 from './components/DayOfWeekChartV2';

/**
 * Sort indicator component
 */
const SortIndicator = ({ sorted }) => {
  if (!sorted) return null;
  return (
    <span style={{ marginLeft: 4, fontSize: '0.75rem' }}>
      {sorted === 'asc' ? '▲' : '▼'}
    </span>
  );
};

/**
 * Loading skeleton row component
 */
const SkeletonRow = ({ columnCount }) => (
  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
    {Array.from({ length: columnCount }).map((_, index) => (
      <td key={index} style={{ padding: '12px 16px' }}>
        <Skeleton variant="text" width={index === 0 ? '80%' : '60%'} height={24} />
      </td>
    ))}
  </tr>
);

/**
 * Tab panel wrapper component
 */
const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box role="tabpanel" sx={{ pt: 2 }}>
      {children}
    </Box>
  );
};

/**
 * Trend indicator component for performer metrics
 * Similar to HierarchyTableV2 TrendIndicator
 */
const TrendIndicator = ({ deltaPct, colors, invertColors = false, neutralOnly = false }) => {
  if (deltaPct === null || deltaPct === undefined) return null;

  const isPositive = deltaPct > 0;
  const isNegative = deltaPct < 0;

  let Icon;
  let color;

  if (neutralOnly) {
    Icon = isPositive ? TrendingUpIcon : isNegative ? TrendingDownIcon : TrendingFlatIcon;
    color = colors.neutral;
  } else if (invertColors) {
    // For metrics where lower is better (labor $, labor %)
    Icon = isPositive ? TrendingUpIcon : isNegative ? TrendingDownIcon : TrendingFlatIcon;
    color = isPositive ? colors.negative : isNegative ? colors.positive : colors.neutral;
  } else {
    Icon = isPositive ? TrendingUpIcon : isNegative ? TrendingDownIcon : TrendingFlatIcon;
    color = isPositive ? colors.positive : isNegative ? colors.negative : colors.neutral;
  }

  const sign = deltaPct >= 0 ? '+' : '';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
      <Icon sx={{ fontSize: 14, color }} />
      <Typography variant="caption" sx={{ color, fontWeight: 500, fontSize: '0.65rem' }}>
        {sign}{deltaPct.toFixed(1)}%
      </Typography>
    </Box>
  );
};

/**
 * MetricCell component for performer table cells
 * Displays current value with trend indicator and comparison tooltip
 */
const PerformerMetricCell = ({
  current,
  comparison,
  deltaPct,
  formatter,
  colors,
  invertColors = false,
  neutralOnly = false,
}) => {
  const formattedValue = formatter(current);
  const tooltipContent = comparison != null
    ? `Comparison: ${formatter(comparison)} | Δ: ${deltaPct >= 0 ? '+' : ''}${deltaPct?.toFixed(1)}%`
    : '';

  const cellContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2">{formattedValue}</Typography>
      <TrendIndicator
        deltaPct={deltaPct}
        colors={colors}
        invertColors={invertColors}
        neutralOnly={neutralOnly}
      />
    </Box>
  );

  if (comparison != null) {
    return (
      <Tooltip title={tooltipContent} placement="top" arrow>
        {cellContent}
      </Tooltip>
    );
  }

  return cellContent;
};

/**
 * Performers Table component for Top/Bottom performers
 * Displays all 6 key metrics matching HierarchyTableV2:
 * Location, SPLH, Sales, Checks, Labor Hours, Labor $, Labor %
 */
const PerformersTable = ({ data = [], loading = false, isTop = true, colors }) => {
  const columns = useMemo(() => [
    // 1. Location Name
    {
      accessorKey: 'locationName',
      header: 'Location',
      cell: ({ row, getValue }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {getValue()}
          </Typography>
          {!row.original.eligible && (
            <Chip
              icon={<ExcludedIcon sx={{ fontSize: 12 }} />}
              label={row.original.exclusionReason || 'Low Volume'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'rgba(245, 158, 11, 0.1)',
                color: '#f59e0b',
                '& .MuiChip-icon': { color: '#f59e0b' },
              }}
            />
          )}
        </Box>
      ),
      meta: { align: 'left' },
    },
    // 2. SPLH (primary metric)
    {
      accessorKey: 'splhCurrent',
      header: 'SPLH',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.splhCurrent}
          comparison={row.original.splhComparison}
          deltaPct={row.original.splhDeltaPct}
          formatter={formatSPLH}
          colors={colors}
        />
      ),
      meta: { align: 'center' },
    },
    // 3. Sales
    {
      accessorKey: 'salesCurrent',
      header: 'Sales',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.salesCurrent}
          comparison={row.original.salesComparison}
          deltaPct={row.original.salesDeltaPct}
          formatter={(v) => formatCurrency(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          colors={colors}
        />
      ),
      meta: { align: 'center' },
    },
    // 4. Check Count
    {
      accessorKey: 'checkCountCurrent',
      header: 'Checks',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.checkCountCurrent}
          comparison={row.original.checkCountComparison}
          deltaPct={row.original.checkCountDeltaPct}
          formatter={(v) => formatNumber(v, { decimalPlaces: 0 })}
          colors={colors}
        />
      ),
      meta: { align: 'center' },
    },
    // 5. Labor Hours - neutral colors (gray arrows)
    {
      accessorKey: 'hoursCurrent',
      header: 'Labor Hours',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.hoursCurrent}
          comparison={row.original.hoursComparison}
          deltaPct={row.original.hoursDeltaPct}
          formatter={(v) => formatNumber(v, { decimalPlaces: 1 })}
          colors={colors}
          neutralOnly={true}
        />
      ),
      meta: { align: 'center' },
    },
    // 6. Labor Dollars - inverted colors (up is bad)
    {
      accessorKey: 'laborDollarsCurrent',
      header: 'Labor $',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.laborDollarsCurrent}
          comparison={row.original.laborDollarsComparison}
          deltaPct={row.original.laborDollarsDeltaPct}
          formatter={(v) => formatCurrency(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          colors={colors}
          invertColors={true}
        />
      ),
      meta: { align: 'center' },
    },
    // 7. Labor Percentage - inverted colors (up is bad)
    {
      accessorKey: 'laborPctCurrent',
      header: 'Labor %',
      cell: ({ row }) => (
        <PerformerMetricCell
          current={row.original.laborPctCurrent}
          comparison={row.original.laborPctComparison}
          deltaPct={row.original.laborPctDeltaPct}
          formatter={(v) => formatPercentage(v / 100, { decimalPlaces: 1 })}
          colors={colors}
          invertColors={true}
        />
      ),
      meta: { align: 'center' },
    },
  ], [colors]);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'splhCurrent', desc: isTop }],
    },
  });

  if (loading) {
    return (
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              {columns.map((col, idx) => (
                <th key={idx} style={{ padding: '12px 16px', textAlign: col.meta?.align || 'left', fontWeight: 600, backgroundColor: '#fafafa' }}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonRow key={idx} columnCount={columns.length} />
            ))}
          </tbody>
        </table>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, bgcolor: '#fafafa', borderRadius: 1 }}>
        <Typography variant="body1" color="text.secondary">No {isTop ? 'top' : 'bottom'} performers data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ borderBottom: '2px solid #e0e0e0' }}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    padding: '12px 16px',
                    textAlign: header.column.columnDef.meta?.align || 'left',
                    fontWeight: 600,
                    backgroundColor: '#fafafa',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: header.column.columnDef.meta?.align === 'center' ? 'center' : header.column.columnDef.meta?.align === 'right' ? 'flex-end' : 'flex-start' }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && <SortIndicator sorted={header.column.getIsSorted()} />}
                  </Box>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} style={{ borderBottom: '1px solid #e0e0e0', opacity: row.original.eligible !== false ? 1 : 0.6 }}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={{ padding: '12px 16px', textAlign: cell.column.columnDef.meta?.align || 'left' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

PerformersTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  isTop: PropTypes.bool,
  colors: PropTypes.object.isRequired,
};

/**
 * Breakdown dimension table (Day of Week, Daypart)
 */
const DimensionTable = ({ data = [], dimensionLabel, loading = false, colors }) => {
  const columns = useMemo(() => [
    {
      accessorKey: 'label',
      header: dimensionLabel,
      cell: ({ getValue }) => (
        <Typography variant="body2" fontWeight={500}>{getValue()}</Typography>
      ),
      meta: { align: 'left' },
    },
    {
      accessorKey: 'currentSPLH',
      header: 'Current SPLH',
      cell: ({ getValue }) => (
        <Typography variant="body2">{formatSPLH(getValue())}</Typography>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'comparisonSPLH',
      header: 'Comparison SPLH',
      cell: ({ getValue }) => (
        <Typography variant="body2" color="text.secondary">{formatSPLH(getValue())}</Typography>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'deltaPct',
      header: 'Change',
      cell: ({ getValue }) => {
        const value = getValue();
        const isPositive = value >= 0;
        const color = isPositive ? colors.positive : colors.negative;
        const sign = isPositive ? '+' : '';
        return (
          <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
            {sign}{value?.toFixed(1)}%
          </Typography>
        );
      },
      meta: { align: 'right' },
    },
    {
      accessorKey: 'currentSales',
      header: 'Sales',
      cell: ({ getValue }) => (
        <Typography variant="body2">{formatCurrency(getValue(), { minimumFractionDigits: 0 })}</Typography>
      ),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'currentHours',
      header: 'Hours',
      cell: ({ getValue }) => (
        <Typography variant="body2">{formatNumber(getValue(), { decimalPlaces: 1 })}</Typography>
      ),
      meta: { align: 'right' },
    },
  ], [dimensionLabel, colors]);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) {
    return (
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              {columns.map((col, idx) => (
                <th key={idx} style={{ padding: '12px 16px', textAlign: col.meta?.align || 'left', fontWeight: 600, backgroundColor: '#fafafa' }}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonRow key={idx} columnCount={columns.length} />
            ))}
          </tbody>
        </table>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, bgcolor: '#fafafa', borderRadius: 1 }}>
        <Typography variant="body1" color="text.secondary">No breakdown data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ borderBottom: '2px solid #e0e0e0' }}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    padding: '12px 16px',
                    textAlign: header.column.columnDef.meta?.align || 'left',
                    fontWeight: 600,
                    backgroundColor: '#fafafa',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: header.column.columnDef.meta?.align === 'right' ? 'flex-end' : 'flex-start' }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && <SortIndicator sorted={header.column.getIsSorted()} />}
                  </Box>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={{ padding: '12px 16px', textAlign: cell.column.columnDef.meta?.align || 'left' }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

DimensionTable.propTypes = {
  data: PropTypes.array,
  dimensionLabel: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  colors: PropTypes.object.isRequired,
};

/**
 * BreakdownSectionV2 Component
 */
const BreakdownSectionV2 = ({ breakdownData, loading = false, colors }) => {
  const [activeTab, setActiveTab] = useState(0);

  const chartColors = colors || {
    positive: '#10B981',
    negative: '#EF4444',
    neutral: '#6B7280',
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [
    { key: 'daypart', label: 'Daypart' },
    { key: 'dayOfWeek', label: 'Day of Week' },
    { key: 'topPerformers', label: 'Top Performers' },
    { key: 'bottomPerformers', label: 'Bottom Performers' },
  ];

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Breakdown analysis tabs"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabs-indicator': { display: 'none' },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.key}
            label={tab.label}
            id={`breakdown-tab-${index}`}
            aria-controls={`breakdown-tabpanel-${index}`}
            sx={{
              textTransform: 'none',
              minWidth: 120,
              fontWeight: 500,
              borderRadius: '8px 8px 0 0',
              mx: 0.5,
              color: 'text.secondary',
              '&.Mui-selected': {
                backgroundColor: '#7C3AED',
                color: '#ffffff !important',
              },
              '&:hover:not(.Mui-selected)': {
                backgroundColor: 'rgba(124, 58, 237, 0.08)',
              },
            }}
          />
        ))}
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <DaypartChartV2
          data={breakdownData?.byDaypart}
          loading={loading}
          height="300px"
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <DayOfWeekChartV2
          data={breakdownData?.byDayOfWeek}
          loading={loading}
          height="300px"
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <PerformersTable
          data={breakdownData?.topPerformers}
          loading={loading}
          isTop={true}
          colors={chartColors}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <PerformersTable
          data={breakdownData?.bottomPerformers}
          loading={loading}
          isTop={false}
          colors={chartColors}
        />
      </TabPanel>
    </Box>
  );
};

BreakdownSectionV2.propTypes = {
  /** Breakdown data from backend V2 API */
  breakdownData: PropTypes.shape({
    topPerformers: PropTypes.array,
    bottomPerformers: PropTypes.array,
    byDayOfWeek: PropTypes.array,
    byDaypart: PropTypes.array,
    excludedCount: PropTypes.number,
  }),
  /** Whether data is loading */
  loading: PropTypes.bool,
  /** Color configuration */
  colors: PropTypes.shape({
    positive: PropTypes.string,
    negative: PropTypes.string,
    neutral: PropTypes.string,
  }),
};

export default BreakdownSectionV2;
